import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { setNavigateFn } from './services/navigationService';
import ErrorBoundary from './components/common/ErrorBoundary';
import AdminLayout from './layout/AdminLayout';
import { AuthProvider } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { ToastProvider } from './contexts/ToastContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import LoginPage from './features/auth/LoginPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import ExamList from './features/exam/ExamList';
import ExamForm from './features/exam/ExamForm';
import ReadingList from './features/reading/ReadingList';
import ReadingForm from './features/reading/ReadingForm';
import ListeningList from './features/listening/ListeningList';
import ListeningForm from './features/listening/ListeningForm';
import ReadingQuestionSetList from './features/reading/ReadingQuestionSetList';
import ListeningQuestionSetList from './features/listening/ListeningQuestionSetList';
import ListeningQuestionSetForm from './features/listening/ListeningQuestionSetForm';
import ReadingQuestionList from './features/reading/ReadingQuestionList';
import ListeningQuestionList from './features/listening/ListeningQuestionList';
import ListeningQuestionForm from './features/listening/ListeningQuestionForm';
import WritingList from './features/writing/WritingList';
import WritingForm from './features/writing/WritingForm';
import WritingQuestionsList from './features/writing/WritingQuestionsList';
import WritingQuestionForm from './features/writing/WritingQuestionForm';
import SpeakingList from './features/speaking/SpeakingList';
import SpeakingForm from './features/speaking/SpeakingForm';
import SpeakingFeedbackList from './features/speaking/SpeakingFeedbackList';
import SpeakingFeedbackForm from './features/speaking/SpeakingFeedbackForm';
import InstructorList from './features/instructor/InstructorList';
import InstructorForm from './features/instructor/InstructorForm';
import ReadingQuestionSetForm from './features/reading/ReadingQuestionSetForm';
import ReadingQuestionForm from './features/reading/ReadingQuestionForm';
import BlogList from './features/blog/BlogList';
import BlogFormPage from './features/blog/BlogFormPage';
import AdminDashboard from './features/management/AdminDashboard';
import UsersManagement from './features/management/UsersManagement';
import RegistrationsManagement from './features/management/RegistrationsManagement';
import ScoresManagement from './features/management/ScoresManagement';
import SpeakingSessionsManagement from './features/management/SpeakingSessionsManagement';
import ReferralsManagement from './features/management/ReferralsManagement';
import ReadingAnswersManagement from './features/management/ReadingAnswersManagement';
import ListeningAnswersManagement from './features/management/ListeningAnswersManagement';
import WritingAnswersManagement from './features/management/WritingAnswersManagement';
import ProductSessionsManagement from './features/management/ProductSessionsManagement';
import SessionsManagement from './features/management/SessionsManagement';
import FeedbacksManagement from './features/management/FeedbacksManagement';
import InvoicesManagement from './features/management/InvoicesManagement';
import PartnerStudentsManagement from './features/management/PartnerStudentsManagement';
import PartnerMembersManagement from './features/management/PartnerMembersManagement';

const NavigationSetter = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigateFn(navigate);
  }, [navigate]);
  return null;
};

const App = () => {
  return (
    <DarkModeProvider>
      <ToastProvider>
        <AuthProvider>
          <AdminProvider>
            <LayoutProvider>
              <ErrorBoundary>
              <Router>
                <NavigationSetter />
                <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                
                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="/admin-dashboard" replace />} />

                  {/* Blog Routes */}
                  <Route path="blogs" element={<BlogList />} />
                  <Route path="blogs/new" element={<BlogFormPage />} />
                  <Route path="blogs/:id/edit" element={<BlogFormPage />} />

                  {/* Management Routes */}
                  <Route path="admin-dashboard" element={<AdminDashboard />} />
                  <Route path="users-management" element={<UsersManagement />} />
                  <Route path="registrations-management" element={<RegistrationsManagement />} />
                  <Route path="scores-management" element={<ScoresManagement />} />
                  <Route path="speaking-sessions" element={<SpeakingSessionsManagement />} />
                  <Route path="referrals-management" element={<ReferralsManagement />} />
                  <Route path="reading-answers" element={<ReadingAnswersManagement />} />
                  <Route path="listening-answers" element={<ListeningAnswersManagement />} />
                  <Route path="writing-answers" element={<WritingAnswersManagement />} />
                  <Route path="product-sessions" element={<ProductSessionsManagement />} />
                  <Route path="sessions-management" element={<SessionsManagement />} />
                  <Route path="invoices-management" element={<InvoicesManagement />} />
                  <Route path="feedbacks" element={<FeedbacksManagement />} />

                  {/* Partner Management Routes */}
                  <Route path="partner-students" element={<PartnerStudentsManagement />} />
                  <Route path="partner-members" element={<PartnerMembersManagement />} />

                  {/* Exam Routes */}
                  <Route path="exams" element={<ExamList />} />
                  <Route path="exams/new" element={<ExamForm />} />
                  <Route path="exams/:id/edit" element={<ExamForm />} />

                  {/* Reading Routes */}
                  <Route path="readings" element={<ReadingList />} />
                  <Route path="readings/new" element={<ReadingForm />} />
                  <Route path="readings/:id/edit" element={<ReadingForm />} />

                  {/* Listening Routes */}
                  <Route path="listenings" element={<ListeningList />} />
                  <Route path="listenings/new" element={<ListeningForm />} />
                  <Route path="listenings/:id/edit" element={<ListeningForm />} />

                  {/* Speaking Question Routes */}
                  <Route path="/speakings" element={<SpeakingList />} />
                  <Route path="/speakings/new" element={<SpeakingForm />} />
                  <Route path="/speakings/:id/edit" element={<SpeakingForm />} />

                  {/* Question Set Routes */}
                  <Route
                    path="readings/:readingId/question-sets"
                    element={<ReadingQuestionSetList />}
                  />
                  <Route
                    path="readings/:readingId/question-sets/new"
                    element={<ReadingQuestionSetForm />}
                  />
                  <Route
                    path="readings/:readingId/question-sets/:questionSetId/edit"
                    element={<ReadingQuestionSetForm />}
                  />

                  {/* Question Routes */}
                  <Route
                    path="readings/:readingId/question-sets/:questionSetId/questions"
                    element={<ReadingQuestionList />}
                  />
                  <Route
                    path="readings/:readingId/:passage/question-sets/:questionSetId/questions/new"
                    element={<ReadingQuestionForm />}
                  />
                  <Route
                    path="readings/:readingId/:passage/question-sets/:questionSetId/questions/:questionId/edit"
                    element={<ReadingQuestionForm />}
                  />

                  {/* Listening Question Set Routes */}
                  <Route
                    path="listenings/:listeningId/question-sets"
                    element={<ListeningQuestionSetList />}
                  />
                  <Route
                    path="listenings/:listeningId/question-sets/:questionSetId/edit"
                    element={<ListeningQuestionSetForm />}
                  />
                  <Route
                    path="listenings/:listeningId/question-sets/new"
                    element={<ListeningQuestionSetForm />}
                  />

                  {/* Listening Question Routes */}
                  <Route
                    path="listenings/:listeningId/question-sets/:questionSetId/questions"
                    element={<ListeningQuestionList />}
                  />
                  <Route
                    path="listenings/:listeningId/:part/question-sets/:questionSetId/questions/:questionId/edit"
                    element={<ListeningQuestionForm />}
                  />
                  <Route
                    path="listenings/:listeningId/:part/question-sets/:questionSetId/questions/new"
                    element={<ListeningQuestionForm />}
                  />

                  {/* Writing Question Routes */}
                  <Route path="/writings" element={<WritingList />} />
                  <Route path="/writings/new" element={<WritingForm />} />
                  <Route path="/writings/:id/edit" element={<WritingForm />} />
                  <Route path="/writings/:writingId/questions" element={<WritingQuestionsList />} />
                  <Route path="/writings/:writingId/questions/new" element={<WritingQuestionForm />} />
                  <Route path="/writings/:writingId/questions/:questionId/edit" element={<WritingQuestionForm />} />

                  {/* Speaking Feedback Routes */}
                  <Route path="/speaking-feedback" element={<SpeakingFeedbackList />} />
                  <Route path="/speaking-feedback/:id/feedback" element={<SpeakingFeedbackForm />} />

                  {/* Instructors Routes */}
                  <Route path="/instructors" element={<InstructorList />} />
                  <Route path="/instructors/new" element={<InstructorForm />} />
                  <Route path="/instructors/:id/edit" element={<InstructorForm />} />
                </Route>
                
                {/* Redirect any unknown routes to login */}
                <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </Router>
              </ErrorBoundary>
            </LayoutProvider>
          </AdminProvider>
        </AuthProvider>
      </ToastProvider>
    </DarkModeProvider>
  );
};

export default App;
